import React, { useMemo, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Collapse,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  alpha,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  allModuleMenus,
  NavigationMenuItem,
} from '../config/navigationMenu';
import { administrationMenuItem, isAdministracionPath } from '../config/adminNavigation';
import { maybePersistModuleBackOnNavigate } from '../utils/moduleNavigationContext';
import { useAuth } from '../contexts/AuthContext';
import { canManageSistemaUsers, effectiveUserRoles } from '../utils/sistemaAuthz';
import { filterMenuByPermissions } from '../utils/filterMenuByPermissions';
import { usePermissions } from '../contexts/PermissionContext';

export const SIDEBAR_DRAWER_WIDTH = 280;
export const SIDEBAR_DRAWER_WIDTH_COLLAPSED = 72;

const drawerWidth = SIDEBAR_DRAWER_WIDTH;
const drawerWidthCollapsed = SIDEBAR_DRAWER_WIDTH_COLLAPSED;

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  desktopOpen?: boolean;
  onDesktopToggle?: () => void;
}

/** Clic que debe navegar en la misma pestaña (no Ctrl/Cmd/clic medio / etc.). */
const isPlainLeftClick = (event: React.MouseEvent): boolean =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

// Función para obtener el color del icono según el tipo de item
const getIconColor = (itemText: string, isActive: boolean): string => {
  return '#00548f'; // Azul #00548f para todos los iconos
};

// Función para obtener el color del icono de subitem
const getSubIconColor = (parentText: string, isActive: boolean): string => {
  return '#00548f'; // Azul #00548f para todos los iconos de subitems
};


/** Sidebar: «Informes» va al hub; rutas hijas siguen resaltando el ítem Informes. */
const pathMatchesContabilidadInformesHub = (pathname: string): boolean =>
  ['/contabilidad/informes', '/contabilidad/estado-resultados', '/contabilidad/situacion-patrimonial'].some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );

/** Coincidencia con ítem de menú o cualquier descendiente (submenús anidados). */
const menuItemMatchesPath = (item: NavigationMenuItem, pathname: string): boolean => {
  if (item.path === pathname) return true;
  return item.subItems?.some((child) => menuItemMatchesPath(child, pathname)) ?? false;
};

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose, desktopOpen = true, onDesktopToggle }) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { canPage } = usePermissions();
  const puedeAdministrar = canManageSistemaUsers(effectiveUserRoles(user?.roles));

  const menuItems = useMemo((): NavigationMenuItem[] => {
    const items = filterMenuByPermissions([...allModuleMenus()], (path) => canPage(path));
    // Administración: por rol (Admin), no por catálogo de permisos.
    if (puedeAdministrar) items.push(administrationMenuItem);
    return items;
  }, [puedeAdministrar, canPage]);
  
  // Estado para expandir/cerrar submenús
  const [inventarioOpen, setInventarioOpen] = useState(
    () =>
      location.pathname.startsWith('/inventario') &&
      !location.pathname.startsWith('/inventario/fashion'),
  );
  const [ventasOpen, setVentasOpen] = useState(() => location.pathname.startsWith('/ventas'));
  const [tesoreriaOpen, setTesoreriaOpen] = useState(() => location.pathname.startsWith('/tesoreria'));
  const [comprasOpen, setComprasOpen] = useState(() => location.pathname.startsWith('/compras'));
  const [contabilidadOpen, setContabilidadOpen] = useState(() => location.pathname.startsWith('/contabilidad'));
  const [servicioOpen, setServicioOpen] = useState(() => location.pathname.startsWith('/servicio'));
  const [produccionOpen, setProduccionOpen] = useState(() => location.pathname.startsWith('/produccion'));
  const [fashionOpen, setFashionOpen] = useState(() => location.pathname.startsWith('/inventario/fashion'));

  /** Solo true si el usuario expandió desde contraído tocando un ítem con submenú (luego se contrae al elegir subítem) */
  const openedFromCollapsedRef = useRef(false);

  const handleDesktopIconToggle = () => {
    openedFromCollapsedRef.current = false;
    onDesktopToggle?.();
  };

  /** Con sidebar contraída: solo un submenú abierto; el resto cerrados */
  const openOnlySubmenuFromCollapsed = (itemText: string) => {
    setVentasOpen(itemText === 'Ventas');
    setComprasOpen(itemText === 'Compras');
    setInventarioOpen(itemText === 'Inventario');
    setTesoreriaOpen(itemText === 'Tesorería');
    setContabilidadOpen(itemText === 'Contabilidad');
    setServicioOpen(itemText === 'Servicio');
    setProduccionOpen(itemText === 'Producción');
    setFashionOpen(itemText === 'Fashion');
  };

  /** Efectos tras navegar en la misma pestaña (cerrar drawer mobile / re-colapsar). */
  const applySameTabNavSideEffects = (options?: { collapseIfOpenedFromCollapsed?: boolean }) => {
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
    if (
      options?.collapseIfOpenedFromCollapsed &&
      !isMobile &&
      openedFromCollapsedRef.current &&
      onDesktopToggle
    ) {
      openedFromCollapsedRef.current = false;
      onDesktopToggle();
    }
  };

  const handleItemClick = (item: NavigationMenuItem, event: React.MouseEvent) => {
    // Sidebar contraída en desktop
    if (!isMobile && !desktopOpen && onDesktopToggle) {
      if (!item.subItems) {
        // Navegación la hace <Link>; solo efectos en misma pestaña
        if (isPlainLeftClick(event)) {
          applySameTabNavSideEffects();
        }
        return;
      }
      openedFromCollapsedRef.current = true;
      onDesktopToggle();
      setTimeout(() => {
        openOnlySubmenuFromCollapsed(item.text);
      }, 350);
      return;
    }

    // Comportamiento normal cuando está expandido
    if (item.text === 'Inventario' && item.subItems) {
      setInventarioOpen(!inventarioOpen);
    } else if (item.text === 'Compras' && item.subItems) {
      setComprasOpen(!comprasOpen);
    } else if (item.text === 'Ventas' && item.subItems) {
      setVentasOpen(!ventasOpen);
    } else if (item.text === 'Tesorería' && item.subItems) {
      setTesoreriaOpen(!tesoreriaOpen);
    } else if (item.text === 'Contabilidad' && item.subItems) {
      setContabilidadOpen(!contabilidadOpen);
    } else if (item.text === 'Servicio' && item.subItems) {
      setServicioOpen(!servicioOpen);
    } else if (item.text === 'Producción' && item.subItems) {
      setProduccionOpen(!produccionOpen);
    } else if (item.text === 'Fashion' && item.subItems) {
      setFashionOpen(!fashionOpen);
    } else if (isPlainLeftClick(event)) {
      // Navegación la hace <Link>
      applySameTabNavSideEffects();
    }
  };

  const handleSubItemClick = (
    event: React.MouseEvent,
    parentModulePath: string,
    subItemPath: string,
  ) => {
    if (!isPlainLeftClick(event)) return;
    maybePersistModuleBackOnNavigate(subItemPath, parentModulePath);
    applySameTabNavSideEffects({ collapseIfOpenedFromCollapsed: true });
  };

  const isItemActive = (item: NavigationMenuItem) => {
    if (item.text === 'Administración') {
      return isAdministracionPath(location.pathname);
    }
    return menuItemMatchesPath(item, location.pathname);
  };

  const isSubItemActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const getOpenState = (itemText: string) => {
    switch (itemText) {
      case 'Inventario': return inventarioOpen;
      case 'Ventas': return ventasOpen;
      case 'Tesorería': return tesoreriaOpen;
      case 'Compras': return comprasOpen;
      case 'Contabilidad': return contabilidadOpen;
      case 'Servicio': return servicioOpen;
      case 'Producción': return produccionOpen;
      case 'Fashion': return fashionOpen;
      default: return false;
    }
  };

  const drawer = (
    <Box 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid rgba(0, 0, 0, 0.08)',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
        position: 'relative'
      }}
    >
      {/* Botón para colapsar/expandir sidebar en desktop */}
      {!isMobile && (
        <Tooltip title={desktopOpen ? "Colapsar menú" : "Expandir menú"} placement="right">
          <IconButton
            onClick={handleDesktopIconToggle}
            sx={{
              position: 'absolute',
              top: 8,
              right: desktopOpen ? 8 : '50%',
              transform: desktopOpen ? 'none' : 'translateX(50%)',
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              width: desktopOpen ? 'auto' : 40,
              height: 40,
              color: '#00548f',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            {desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Tooltip>
      )}

      {/* Lista de items - overflowY scroll + scrollbarGutter evitan el "tiritar" al evitar que el scrollbar aparezca/desaparezca */}
      <Box sx={{ 
        flexGrow: 1, 
        overflowX: 'hidden',
        overflowY: 'scroll', // Scrollbar siempre visible = sin layout shift al hacer hover (fix temblor)
        scrollbarGutter: 'stable', // Fallback adicional en navegadores modernos
        py: 1, 
        pt: desktopOpen ? 1 : 9, 
        pb: 7 
      }}>
        <List disablePadding>
          {menuItems.map((item, index) => {
            const isActive = isItemActive(item);
            const isOpen = getOpenState(item.text);
            const hasSubItems = !!item.subItems;

            return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding sx={{ px: desktopOpen ? 1.5 : 0.5, mb: 0.5 }}>
                  <Tooltip 
                    title={!desktopOpen ? item.text : ''} 
                    placement="right"
                    arrow
                  >
                    <ListItemButton
                      {...(hasSubItems
                        ? {}
                        : { component: Link, to: item.path })}
                      onClick={(event) => handleItemClick(item, event)}
                      sx={{
                        borderRadius: 2,
                        py: 1.25,
                        px: desktopOpen ? 2 : 1,
                        minHeight: 48,
                        justifyContent: desktopOpen ? 'flex-start' : 'center',
                        transition: 'all 0.2s ease-in-out',
                        textDecoration: 'none',
                        color: 'inherit',
                        backgroundColor: isActive && !hasSubItems 
                          ? alpha(theme.palette.primary.main, 0.15) 
                          : 'transparent',
                        borderLeft: isActive && !hasSubItems
                          ? `4px solid ${theme.palette.primary.main}`
                          : '4px solid transparent',
                        '&:hover': {
                          backgroundColor: isActive && !hasSubItems
                            ? alpha(theme.palette.primary.main, 0.2)
                            : alpha(theme.palette.primary.main, 0.08),
                          borderLeftColor: isActive && !hasSubItems
                            ? theme.palette.primary.main
                            : alpha(theme.palette.primary.main, 0.5),
                          transform: desktopOpen ? 'translateX(4px)' : 'scale(1.1)',
                        },
                        '&:active': {
                          transform: desktopOpen ? 'translateX(2px)' : 'scale(1.05)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: desktopOpen ? 40 : 'auto',
                          justifyContent: 'center',
                          color: getIconColor(item.text, isActive && !hasSubItems),
                          transition: 'color 0.2s ease-in-out',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {desktopOpen && (
                        <>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontSize: '0.9375rem',
                              fontWeight: isActive && !hasSubItems ? 600 : 500,
                              color: isActive && !hasSubItems
                                ? theme.palette.primary.main
                                : 'text.primary',
                            }}
                          />
                          {hasSubItems && (
                            <Box
                              sx={{
                                color: isActive
                                  ? theme.palette.primary.main
                                  : 'text.secondary',
                                transition: 'transform 0.2s ease-in-out, color 0.2s ease-in-out',
                                transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              }}
                            >
                              <ExpandMore />
                            </Box>
                          )}
                        </>
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>

                {/* Submenús - Solo mostrar si está expandido */}
                {hasSubItems && desktopOpen && (
                  <Collapse 
                    in={isOpen} 
                    timeout="auto" 
                    unmountOnExit
                    sx={{ pl: 2 }}
                  >
                    <List disablePadding>
                      {item.subItems!.map((subItem) => {
                        const isInformesContabilidadSidebarLeaf =
                          item.text === 'Contabilidad' && subItem.text === 'Informes';
                        const isSubActive = isInformesContabilidadSidebarLeaf
                          ? pathMatchesContabilidadInformesHub(location.pathname)
                          : isSubItemActive(subItem.path);
                        return (
                          <ListItem key={subItem.path} disablePadding sx={{ mb: 0.25 }}>
                            <Tooltip 
                              title={subItem.text} 
                              placement="right"
                              arrow
                            >
                              <ListItemButton
                                component={Link}
                                to={subItem.path}
                                onClick={(event) => handleSubItemClick(event, item.path, subItem.path)}
                                sx={{
                                  borderRadius: 1.5,
                                  py: 0.875,
                                  px: 2,
                                  ml: 1,
                                  minHeight: 40,
                                  transition: 'all 0.2s ease-in-out',
                                  textDecoration: 'none',
                                  color: 'inherit',
                                  backgroundColor: isSubActive
                                    ? alpha(theme.palette.primary.main, 0.18)
                                    : 'transparent',
                                  borderLeft: isSubActive
                                    ? `4px solid ${theme.palette.primary.main}`
                                    : '4px solid transparent',
                                  '&:hover': {
                                    backgroundColor: isSubActive
                                      ? alpha(theme.palette.primary.main, 0.25)
                                      : alpha(theme.palette.primary.main, 0.1),
                                    borderLeftColor: isSubActive
                                      ? theme.palette.primary.main
                                      : alpha(theme.palette.primary.main, 0.5),
                                    transform: 'translateX(2px)',
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 36,
                                    color: getSubIconColor(item.text, isSubActive),
                                    fontSize: '1.1rem',
                                  }}
                                >
                                  {subItem.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={subItem.text}
                                  primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: isSubActive ? 600 : 400,
                                    color: isSubActive
                                      ? theme.palette.primary.main
                                      : 'text.primary',
                                  }}
                                />
                              </ListItemButton>
                            </Tooltip>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}

                {/* Separador después de ciertos items - Solo mostrar si está expandido */}
                {desktopOpen && (index === 0 || item.text === 'Compras') && (
                  <Box sx={{ py: 0.5 }}>
                    <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.1) }} />
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ 
        width: { md: desktopOpen ? drawerWidth : drawerWidthCollapsed }, 
        flexShrink: { md: 0 },
        transition: 'width 0.3s ease-in-out'
      }}
    >
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : desktopOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'block' },
          '& .MuiDrawer-paper': {
            width: !isMobile 
              ? (desktopOpen ? drawerWidth : drawerWidthCollapsed)
              : drawerWidth,
            boxSizing: 'border-box',
            mt: isMobile ? 8 : 8,
            background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
            borderRight: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: 'none',
            zIndex: (theme) => theme.zIndex.drawer,
            transition: 'width 0.3s ease-in-out',
            overflowX: 'hidden',
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
